/**
 * Recovery boundary — ONE component, mounted twice:
 *   • src/main.tsx around <App />: a shell crash still leaves a calm panel
 *     with a way to take the data out;
 *   • src/App.tsx around the route switch (keyed by route): a broken view
 *     does not take the sidebar or the export button with it, and navigating
 *     to another section remounts the boundary so that view gets a clean try.
 *
 * Class component on purpose: getDerivedStateFromError/componentDidCatch are
 * the API, and hooks cannot be used inside a fallback. React never invokes
 * error boundaries during server rendering — this panel only ever renders in
 * the browser. It reads the store directly (no subscription needed: it is the
 * fallback, it does not follow store updates).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { translate } from '@/i18n';
import { exportCampaignFile } from '@/persistence/storage';
import { getState } from '@/state/store';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  componentStack: string | null;
}

export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null, componentStack: null };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(_error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? null });
  }

  /** "Download my campaign" — same .atlagm envelope as every other export. */
  private readonly downloadCampaign = (): void => {
    const campaign = getState().campaign;
    if (campaign) exportCampaignFile(campaign);
  };

  private readonly reload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error, componentStack } = this.state;
    if (error === null) return this.props.children;

    const { ui, campaign } = getState();
    const t = (key: string): string => translate(ui.locale, key);

    return (
      <section className="card crash-panel" role="alert">
        <h2>{t('crash.title')}</h2>
        <p className="muted">{t('crash.body')}</p>
        <div className="btn-row">
          <button
            type="button"
            className="btn"
            disabled={campaign === null}
            onClick={this.downloadCampaign}
          >
            {t('crash.download')}
          </button>
          <button type="button" className="btn secondary" onClick={this.reload}>
            {t('crash.reload')}
          </button>
        </div>
        <details className="crash-details">
          <summary>{t('crash.details')}</summary>
          <pre>{error.stack ?? String(error)}</pre>
          {componentStack !== null ? <pre>{componentStack}</pre> : null}
        </details>
      </section>
    );
  }
}
